import type { FormEvent, ReactNode } from "react";
import { PackageCheck, PackagePlus, Store, TicketCheck } from "lucide-react";
import type { PublicMapArea, PublicPermitProduct } from "@natural/public-contract";
import type { OrganizationDetail } from "../../account";

type FormHandler = (event: FormEvent<HTMLFormElement>) => void;

type OrganizationPermitsPageProps = {
  areas: PublicMapArea[];
  canEdit: boolean;
  canUsePermitProducts: boolean;
  data: OrganizationDetail;
  isBlocked: (id: string) => boolean;
  linkProduct: FormHandler;
  locked: (id: string) => ReactNode;
  offer: FormHandler;
  onSelectedProductChange: (value: string) => void;
  packageLocked: (title: string, description: string) => ReactNode;
  permitSalesAvailable: boolean;
  productCapacityAvailable: boolean;
  products: PublicPermitProduct[];
  proposePermitProduct: FormHandler;
  selectedProduct: string;
  verifiedAreas: OrganizationDetail["areas"];
};

export function OrganizationPermitsPage({
  areas,
  canEdit,
  canUsePermitProducts,
  data,
  isBlocked,
  linkProduct,
  locked,
  offer,
  onSelectedProductChange,
  packageLocked,
  permitSalesAvailable,
  productCapacityAvailable,
  products,
  proposePermitProduct,
  selectedProduct,
  verifiedAreas,
}: OrganizationPermitsPageProps) {
  const areaName = (id: unknown) =>
    areas.find((area) => area.id === String(id))?.name ?? String(id);
  const productName = (id: unknown) =>
    products.find((product) => product.id === String(id))?.name ?? String(id);
  const productApplicationStatus = (status: unknown) =>
    ({
      submitted: "Odottaa Platformin tarkastusta",
      under_review: "Platform käsittelee",
      approved: "Core-luonnos luotu · odottaa erillistä aktivointia",
      returned: "Palautettu täydennettäväksi",
      rejected: "Hylätty",
    })[String(status)] ?? String(status);
  const verifiedSellerProductIds = new Set(
    data.permitProducts
      .filter((item) => item.relationship === "seller" && item.status === "verified")
      .map((item) => String(item.permit_product_id)),
  );
  const issuerChoices = data.issuerOptions.filter(
    (item) =>
      !selectedProduct || String(item.permit_product_id) === selectedProduct,
  );
  return (
            <div className="workspace-content">
              {data.onboarding.goals.includes("permit_issuing") && (
                <section className="account-panel workspace-section">
                  <div className="workspace-section-heading">
                    <div>
                      <span className="eyebrow">Uusi lupatuote</span>
                      <h2>Ehdota organisaation omaa lupaa</h2>
                      <p>
                        Hakemus sidotaan varmennettuun Core-alueeseen. Platform
                        tarkistaa lähteen ja ehdot ennen erillistä
                        Core-luonnosta.
                      </p>
                    </div>
                    <PackagePlus />
                  </div>
                  <p className="workspace-boundary-note">
                    Hakemus, alustava hinta tai Platformin hyväksyntä eivät
                    julkaise tuotetta eivätkä avaa maksamista. Core-tuote
                    aktivoidaan ja myyntitarjous julkaistaan erikseen.
                  </p>
                  {data.productApplications.length > 0 && (
                    <div className="organization-list">
                      {data.productApplications.map((item) => (
                        <article key={String(item.id)}>
                          <PackageCheck />
                          <div>
                            <b>{String(item.name)}</b>
                            <span>
                              {areaName(item.core_area_id)} ·{" "}
                              {productApplicationStatus(item.status)}
                            </span>
                            {Boolean(item.review_note) && (
                              <small>{String(item.review_note)}</small>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                  {!productCapacityAvailable
                    ? packageLocked(
                        "Lupatuoteraja on täynnä",
                        "Nykyinen palvelupaketti ei salli uutta lupatuotehakemusta.",
                      )
                    : verifiedAreas.length === 0
                      ? locked("areas")
                      : canEdit && (
                          <form
                            className="inline-account-form workspace-form"
                            onSubmit={proposePermitProduct}
                          >
                            <label>
                              Varmennettu Core-alue
                              <select name="coreAreaId" required defaultValue="">
                                <option value="" disabled>
                                  Valitse alue
                                </option>
                                {verifiedAreas.map((area) => (
                                  <option
                                    value={String(area.core_area_id)}
                                    key={String(area.id)}
                                  >
                                    {areaName(area.core_area_id)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              Lupatuotteen nimi
                              <input name="name" required minLength={2} maxLength={200} />
                            </label>
                            <label>
                              Kalastustapa
                              <select name="activity" required defaultValue="lure_fishing">
                                <option value="lure_fishing">Viehekalastus</option>
                                <option value="angling">Onkiminen</option>
                                <option value="net_fishing">Verkkokalastus</option>
                                <option value="trap_fishing">Pyydyskalastus</option>
                                <option value="trolling">Vetouistelu</option>
                                <option value="general_fishing">Yleinen kalastus</option>
                                <option value="other">Muu</option>
                              </select>
                            </label>
                            <label>
                              Väline
                              <select name="method" required defaultValue="rod">
                                <option value="rod">Vapa</option>
                                <option value="net">Verkko</option>
                                <option value="trap">Pyydys</option>
                                <option value="trolling">Vetouistelu</option>
                                <option value="hook_and_line">Koukku ja siima</option>
                                <option value="other">Muu</option>
                              </select>
                            </label>
                            <label>
                              Voimassa alkaen
                              <input name="validFrom" type="date" required />
                            </label>
                            <label>
                              Voimassa asti
                              <input name="validTo" type="date" required />
                            </label>
                            <label>
                              Kiintiö <small>valinnainen</small>
                              <input name="quotaLimit" type="number" min="1" step="1" />
                            </label>
                            <label>
                              Kiintiöjakso
                              <select name="quotaPeriod" defaultValue="season">
                                <option value="season">Kausi</option>
                                <option value="day">Päivä</option>
                              </select>
                            </label>
                            <label>
                              Alustava hinta euroina <small>valinnainen</small>
                              <input name="proposedPrice" type="number" min="0" step="0.01" />
                            </label>
                            <label>
                              Virallinen lähde tai päätös (HTTPS)
                              <input name="sourceUrl" type="url" pattern="https://.*" required />
                            </label>
                            <label className="wide-field">
                              Päätös- tai pöytäkirjaviite
                              <textarea name="decisionReference" required minLength={4} maxLength={500} rows={2} />
                            </label>
                            <div>
                              <button>Lähetä lupatuotehakemus</button>
                            </div>
                          </form>
                        )}
                </section>
              )}
              <section className="account-panel workspace-section">
                <div className="workspace-section-heading">
                  <div>
                    <span className="eyebrow">Lupatuotteet</span>
                    <h2>Organisaation rooli tuotteissa</h2>
                    <p>
                      Luvanmyöntäjän ja myyjän suhteet varmennetaan erikseen.
                    </p>
                  </div>
                  <TicketCheck />
                </div>
                <p className="workspace-boundary-note">
                  Tuotekytkentä kertoo, kuka myöntää tai myy luvan. Se ei anna
                  organisaation jäsenille henkilökohtaista kalastuslupaa.
                  Henkilökohtaiset luvat näkyvät erikseen käyttäjän omalla
                  tilillä ja Core huomioi vain varmennetut luvat.
                </p>
                {data.permitProducts.length ? (
                  <div className="organization-list">
                    {data.permitProducts.map((item, index) => (
                      <article key={index}>
                        <TicketCheck />
                        <div>
                          <b>{productName(item.permit_product_id)}</b>
                          <span>
                            {item.relationship === "issuer"
                              ? "Luvanmyöntäjä"
                              : "Luvanmyyjä"}{" "}
                            ·{" "}
                            {item.status === "verified"
                              ? "varmennettu"
                              : "odottaa varmennusta"}
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="account-empty compact">
                    <TicketCheck />
                    <p>Lupatuotekytkentöjä ei ole vielä.</p>
                  </div>
                )}
                {!canUsePermitProducts
                  ? packageLocked(
                      "Lupatuotteiden hallinta ei kuulu pakettiin",
                      "Valitse lupien myöntämistä tai myyntiä tukeva palvelupaketti.",
                    )
                  : !productCapacityAvailable
                  ? packageLocked(
                      "Lupatuoteraja on täynnä",
                      "Nykyinen palvelupaketti ei salli uusien lupatuotteiden lisäämistä.",
                    )
                  : isBlocked("products")
                  ? locked("products")
                  : canEdit && (
                      <form
                        className="inline-account-form workspace-form"
                        onSubmit={linkProduct}
                      >
                        <label>
                          Julkaistu lupatuote
                          <select
                            name="permitProductId"
                            required
                            defaultValue=""
                          >
                            <option value="" disabled>
                              Valitse lupatuote
                            </option>
                            {products.map((product) => (
                              <option value={product.id} key={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Rooli tuotteessa
                          <select
                            name="relationship"
                            defaultValue={
                              data.onboarding.goals.includes("permit_issuing")
                                ? "issuer"
                                : "seller"
                            }
                          >
                            {data.onboarding.goals.includes(
                              "permit_issuing",
                            ) && <option value="issuer">Luvanmyöntäjä</option>}
                            {data.onboarding.goals.includes("permit_sales") &&
                              permitSalesAvailable && (
                              <option value="seller">Luvanmyyjä</option>
                            )}
                          </select>
                        </label>
                        <div>
                          <button>Liitä varmennettavaksi</button>
                        </div>
                      </form>
                    )}
              </section>
              {data.onboarding.goals.includes("permit_sales") &&
                permitSalesAvailable && (
                <section className="account-panel workspace-section">
                  <span className="eyebrow">Myynti</span>
                  <h2>Tarjousluonnokset</h2>
                  <p className="workspace-boundary-note">
                    Luonnoksen voi valmistella ennen Stripeä. Se ei näy
                    julkisessa katalogissa eikä sitä voi ostaa ennen
                    maksutilin ja kaikkien kaupparajojen varmennusta.
                  </p>
                  {data.offers.length ? (
                    <div className="account-item-list">
                      {data.offers.map((item) => (
                        <article key={String(item.id)}>
                          <Store />
                          <div>
                            <b>{String(item.display_name)}</b>
                            <span>
                              {(Number(item.unit_amount) / 100).toLocaleString(
                                "fi-FI",
                                {
                                  style: "currency",
                                  currency: String(item.currency),
                                },
                              )}{" "}
                              ·{" "}
                              {item.status === "active"
                                ? "myynnissä"
                                : "luonnos"}
                            </span>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="account-empty compact">
                      <Store />
                      <p>Myyntitarjouksia ei ole vielä.</p>
                    </div>
                  )}
                  {isBlocked("offer")
                    ? locked("offer")
                    : canEdit && (
                        <form
                          className="inline-account-form workspace-form"
                          onSubmit={offer}
                        >
                          <label>
                            Lupatuote
                            <select
                              name="permitProductId"
                              required
                              value={selectedProduct}
                              onChange={(event) =>
                                onSelectedProductChange(event.target.value)
                              }
                            >
                              <option value="" disabled>
                                Valitse lupatuote
                              </option>
                              {products.filter((product) =>
                                verifiedSellerProductIds.has(product.id),
                              ).map((product) => (
                                <option value={product.id} key={product.id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Luvanmyöntäjä
                            <select
                              name="issuerOrganizationId"
                              required
                              defaultValue=""
                            >
                              <option value="" disabled>
                                Valitse varmennettu luvanmyöntäjä
                              </option>
                              {issuerChoices.map((item) => (
                                <option
                                  value={String(item.id)}
                                  key={`${item.id}-${item.permit_product_id}`}
                                >
                                  {String(item.name)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Myyntinimi
                            <input
                              name="displayName"
                              required
                              minLength={2}
                              maxLength={200}
                            />
                          </label>
                          <label>
                            Hinta euroina
                            <input
                              name="price"
                              type="number"
                              min="0"
                              step="0.01"
                              required
                            />
                          </label>
                          <label>
                            Alustan osuus euroina
                            <input
                              name="platformFee"
                              type="number"
                              min="0"
                              step="0.01"
                              defaultValue="0"
                              required
                            />
                          </label>
                          <label>
                            Myyntiehdot (HTTPS)
                            <input
                              name="termsUrl"
                              type="url"
                              pattern="https://.*"
                            />
                          </label>
                          <div>
                            <button>Tallenna tarjousluonnos</button>
                          </div>
                        </form>
                      )}
                </section>
              )}
            </div>
  );
}
